import { useLayoutEffect, useRef, type CSSProperties } from "react";

type Size = "sm" | "md" | "lg" | "hero";
type Tone = "ink" | "light" | "gold";

interface PortfolioWordmarkProps {
  size?: Size;
  tone?: Tone;
  /** Third line, "Beachfront Homes". Off in tight header slots. */
  showTagline?: boolean;
  className?: string;
}

const SIZES: Record<Size, { top: string; name: string; tag: string; gapTop: string; gapTag: string }> = {
  sm:   { top: "text-[13px]",                 name: "text-[30px]",                   tag: "text-[8px]",                  gapTop: "mb-[3px]",    gapTag: "mt-[5px]" },
  md:   { top: "text-[16px]",                 name: "text-[40px]",                   tag: "text-[9px]",                  gapTop: "mb-[4px]",    gapTag: "mt-[6px]" },
  lg:   { top: "text-[22px]",                 name: "text-[60px]",                   tag: "text-[11px]",                 gapTop: "mb-[6px]",    gapTag: "mt-[8px]" },
  hero: { top: "text-[clamp(26px,5.6vw,40px)]", name: "text-[clamp(86px,19vw,142px)]", tag: "text-[clamp(15px,3.6vw,26px)]", gapTop: "mb-[0.3em]", gapTag: "mt-[0.5em]" },
};

const TONES: Record<Tone, { main: string; sub: string }> = {
  ink:   { main: "text-foreground", sub: "text-[#5F5747]" },
  light: { main: "text-white",      sub: "text-white/90" },
  gold:  { main: "wordmark-gold",   sub: "wordmark-gold" },
};

/**
 * Vacation Rental / Oahu / Beachfront Homes, set as one block: after render the
 * three lines are measured and the narrower ones are letter-spaced out so all
 * share the width of the widest. Same mark at every size, no hand-tuned tracking.
 */
export function PortfolioWordmark({ size = "md", tone = "ink", showTagline = true, className = "" }: PortfolioWordmarkProps) {
  const topRef = useRef<HTMLSpanElement>(null);
  const nameRef = useRef<HTMLSpanElement>(null);
  const tagRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const lines = [
      { el: topRef.current, chars: "Vacation Rental".length, maxEm: 0.2 },
      { el: nameRef.current, chars: "Oahu".length, maxEm: 0.6 },
      { el: tagRef.current, chars: "Beachfront Homes".length, maxEm: 0.6 },
    ].filter((l): l is { el: HTMLSpanElement; chars: number; maxEm: number } => Boolean(l.el));
    if (lines.length < 2) return;

    const fit = () => {
      for (const l of lines) { l.el.style.letterSpacing = "0px"; l.el.style.marginRight = "0px"; }
      const widths = lines.map((l) => l.el.getBoundingClientRect().width);
      const target = Math.max(...widths);
      lines.forEach((l, i) => {
        const fontSize = parseFloat(getComputedStyle(l.el).fontSize) || 16;
        const extra = Math.min((target - widths[i]) / l.chars, l.maxEm * fontSize);
        if (extra > 0.25) {
          l.el.style.letterSpacing = `${extra}px`;
          l.el.style.marginRight = `${-extra}px`; // spacing is added after the last glyph too
        }
      });
    };
    fit();
    const ro = new ResizeObserver(fit);
    lines.forEach((l) => ro.observe(l.el));
    window.addEventListener("resize", fit);
    document.fonts?.ready.then(fit).catch(() => {});
    return () => { ro.disconnect(); window.removeEventListener("resize", fit); };
  }, [size, showTagline]);

  const s = SIZES[size];
  const t = TONES[tone];
  const lineStyle: CSSProperties = { display: "block", whiteSpace: "nowrap", width: "max-content" };

  return (
    <span className={`inline-flex flex-col items-start leading-none ${className}`} role="img" aria-label="Vacation Rental Oahu, beachfront homes">
      <span ref={topRef} style={lineStyle} className={`font-serif font-[500] ${s.top} ${s.gapTop} ${t.sub}`} aria-hidden="true">
        Vacation Rental
      </span>
      <span ref={nameRef} style={lineStyle} className={`font-serif font-[600] leading-[0.88] ${s.name} ${t.main}`} aria-hidden="true">
        Oahu
      </span>
      {showTagline && (
        <span ref={tagRef} style={lineStyle} className={`font-sans font-medium uppercase tracking-[0.3em] ${s.tag} ${s.gapTag} ${t.sub}`} aria-hidden="true">
          Beachfront Homes
        </span>
      )}
    </span>
  );
}
